import type { SyncStatus } from "./sync";

const RETRY_BASE_MS = 5_000;
const RETRY_MULTIPLIER = 3;
const RETRY_MAX_MS = 300_000;

export function syncRetryDelayMs(attempt: number): number {
  return Math.min(RETRY_BASE_MS * RETRY_MULTIPLIER ** attempt, RETRY_MAX_MS);
}

interface SyncRetryDeps<Timer> {
  run: () => void;
  setTimer: (callback: () => void, delayMs: number) => Timer;
  clearTimer: (timer: Timer) => void;
  isHidden: () => boolean;
  isRetryable: (status: SyncStatus) => boolean;
}

export function createSyncRetry<Timer>(deps: SyncRetryDeps<Timer>): {
  onStatus: (status: SyncStatus) => void;
  stop: () => void;
} {
  let attempt = 0;
  let timer: Timer | null = null;
  let stopped = false;
  let latest: SyncStatus | null = null;

  function cancel(): void {
    if (timer !== null) deps.clearTimer(timer);
    timer = null;
  }

  return {
    onStatus(status) {
      latest = status;
      cancel();
      // Idle also covers resetSyncStatus(), before the new account's first pull.
      // Offline resets too: failures before a reconnect say nothing about the
      // network after it.
      if (status.state === "idle" || status.state === "offline") attempt = 0;
      if (stopped || !deps.isRetryable(status) || deps.isHidden()) return;
      timer = deps.setTimer(() => {
        timer = null;
        if (stopped || deps.isHidden() || latest === null || !deps.isRetryable(latest)) return;
        attempt += 1;
        deps.run();
      }, syncRetryDelayMs(attempt));
    },
    stop() {
      stopped = true;
      cancel();
    },
  };
}
